"use client";

import { useEffect } from "react";

const MIN_SWIPE_DISTANCE_PX = 50;
const DIRECTION_LOCK_THRESHOLD_PX = 15;

/**
 * Mobile Swipe-Geste zum Wechseln zwischen Kategorien-Tabs auf der Aktienseite
 * (backlog.md -> Punkt 98, hohe Priorität laut Erik: ~80% Traffic kommt über TikTok/
 * mobil). Sitewide gemountet (gleiches Muster wie SearchKeyboardShortcut/
 * BackToTopButton) statt in `category-tabs.tsx` selbst integriert - vermeidet jede
 * Berührung der als fragil bekannten CategoryTabsSection-Suspense-Architektur (siehe
 * backlog.md). Reagiert NUR innerhalb der einen mit `data-swipeable-tabs` markierten
 * Tabs-Instanz (siehe category-tabs.tsx) - die anderen `<Tabs>`-Vorkommen im
 * Dashboard/Depot bleiben unberührt.
 *
 * Nutzt Radix' eigenes, bereits vorhandenes Klick-Verhalten der TabsTrigger-Buttons
 * (findet den nächsten/vorherigen aktivierten Tab und ruft `.click()` auf) - verwaltet
 * selbst keinerlei Tab-Zustand, keine Änderung an der bestehenden Tabs-Logik. `.click()`
 * statt der browsereigenen Touch-zu-Klick-Synthese, weil `preventDefault()` auf
 * `touchmove` (siehe unten, noetig um Seiten-Scroll waehrend des Swipes zu verhindern)
 * genau diese native Synthese unterdrueckt - der manuelle Aufruf gleicht das aus.
 * Ignoriert Touches, die auf einem Chart (`.recharts-wrapper`) beginnen, um mit dessen
 * eigener Ziehen-Zoom-Geste nicht zu kollidieren. Bricht bei überwiegend vertikaler
 * Bewegung früh ab (kein `preventDefault`), damit normales Scrollen nicht gestört wird.
 */
export function CategoryTabsSwipeHandler() {
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let tracking = false;
    let horizontalLock = false;

    function onTouchStart(event: TouchEvent) {
      const target = event.target as HTMLElement;
      const inSwipeablePanel = target.closest('[data-swipeable-tabs] [role="tabpanel"]');
      if (!inSwipeablePanel || target.closest(".recharts-wrapper")) {
        tracking = false;
        return;
      }
      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
      horizontalLock = false;
    }

    function onTouchMove(event: TouchEvent) {
      if (!tracking) return;
      const touch = event.touches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;

      if (!horizontalLock) {
        if (Math.abs(dx) < DIRECTION_LOCK_THRESHOLD_PX && Math.abs(dy) < DIRECTION_LOCK_THRESHOLD_PX) return;
        if (Math.abs(dx) <= Math.abs(dy) * 1.5) {
          // Ueberwiegend vertikal - normales Scrollen zulassen, diesen Touch nicht mehr verfolgen.
          tracking = false;
          return;
        }
        horizontalLock = true;
      }

      // Eindeutig horizontal erkannt - verhindert Seiten-Scroll waehrend des Swipes.
      event.preventDefault();
    }

    function onTouchEnd(event: TouchEvent) {
      if (!tracking || !horizontalLock) {
        tracking = false;
        return;
      }
      tracking = false;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - startX;
      if (Math.abs(dx) < MIN_SWIPE_DISTANCE_PX) return;
      switchTab(dx < 0 ? 1 : -1);
    }

    function switchTab(step: 1 | -1) {
      const tabs = Array.from(
        document.querySelectorAll<HTMLButtonElement>('[data-swipeable-tabs] [role="tab"]'),
      );
      const activeIndex = tabs.findIndex((tab) => tab.getAttribute("data-state") === "active");
      if (activeIndex === -1) return;
      for (let i = activeIndex + step; i >= 0 && i < tabs.length; i += step) {
        if (!tabs[i].disabled) {
          tabs[i].click();
          tabs[i].scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
          return;
        }
      }
    }

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  return null;
}
